import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Copy, Clock, Home, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { PaymentProofUpload } from "@/components/PaymentProofUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PayNowPayment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const paymentReference = searchParams.get("ref");
  const amount = parseFloat(searchParams.get("amount") || "0");
  const transactionId = searchParams.get("txn");
  
  const [status, setStatus] = useState("pending");
  const [copied, setCopied] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60);
  const [accountHolderName, setAccountHolderName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // CRC16 lookup table for PayNow QR code
  const crcTable = [
    0x0000, 0x1021, 0x2042, 0x3063, 0x4084, 0x50a5,
    0x60c6, 0x70e7, 0x8108, 0x9129, 0xa14a, 0xb16b,
    0xc18c, 0xd1ad, 0xe1ce, 0xf1ef, 0x1231, 0x0210,
    0x3273, 0x2252, 0x52b5, 0x4294, 0x72f7, 0x62d6,
    0x9339, 0x8318, 0xb37b, 0xa35a, 0xd3bd, 0xc39c,
    0xf3ff, 0xe3de, 0x2462, 0x3443, 0x0420, 0x1401,
    0x64e6, 0x74c7, 0x44a4, 0x5485, 0xa56a, 0xb54b,
    0x8528, 0x9509, 0xe5ee, 0xf5cf, 0xc5ac, 0xd58d,
    0x3653, 0x2672, 0x1611, 0x0630, 0x76d7, 0x66f6,
    0x5695, 0x46b4, 0xb75b, 0xa77a, 0x9719, 0x8738,
    0xf7df, 0xe7fe, 0xd79d, 0xc7bc, 0x48c4, 0x58e5,
    0x6886, 0x78a7, 0x0840, 0x1861, 0x2802, 0x3823,
    0xc9cc, 0xd9ed, 0xe98e, 0xf9af, 0x8948, 0x9969,
    0xa90a, 0xb92b, 0x5af5, 0x4ad4, 0x7ab7, 0x6a96,
    0x1a71, 0x0a50, 0x3a33, 0x2a12, 0xdbfd, 0xcbdc,
    0xfbbf, 0xeb9e, 0x9b79, 0x8b58, 0xbb3b, 0xab1a,
    0x6ca6, 0x7c87, 0x4ce4, 0x5cc5, 0x2c22, 0x3c03,
    0x0c60, 0x1c41, 0xedae, 0xfd8f, 0xcdec, 0xddcd,
    0xad2a, 0xbd0b, 0x8d68, 0x9d49, 0x7e97, 0x6eb6,
    0x5ed5, 0x4ef4, 0x3e13, 0x2e32, 0x1e51, 0x0e70,
    0xff9f, 0xefbe, 0xdfdd, 0xcffc, 0xbf1b, 0xaf3a,
    0x9f59, 0x8f78, 0x9188, 0x81a9, 0xb1ca, 0xa1eb,
    0xd10c, 0xc12d, 0xf14e, 0xe16f, 0x1080, 0x00a1,
    0x30c2, 0x20e3, 0x5004, 0x4025, 0x7046, 0x6067,
    0x83b9, 0x9398, 0xa3fb, 0xb3da, 0xc33d, 0xd31c,
    0xe37f, 0xf35e, 0x02b1, 0x1290, 0x22f3, 0x32d2,
    0x4235, 0x5214, 0x6277, 0x7256, 0xb5ea, 0xa5cb,
    0x95a8, 0x8589, 0xf56e, 0xe54f, 0xd52c, 0xc50d,
    0x34e2, 0x24c3, 0x14a0, 0x0481, 0x7466, 0x6447,
    0x5424, 0x4405, 0xa7db, 0xb7fa, 0x8799, 0x97b8,
    0xe75f, 0xf77e, 0xc71d, 0xd73c, 0x26d3, 0x36f2,
    0x0691, 0x16b0, 0x6657, 0x7676, 0x4615, 0x5634,
    0xd94c, 0xc96d, 0xf90e, 0xe92f, 0x99c8, 0x89e9,
    0xb98a, 0xa9ab, 0x5844, 0x4865, 0x7806, 0x6827,
    0x18c0, 0x08e1, 0x3882, 0x28a3, 0xcb7d, 0xdb5c,
    0xeb3f, 0xfb1e, 0x8bf9, 0x9bd8, 0xabbb, 0xbb9a,
    0x4a75, 0x5a54, 0x6a37, 0x7a16, 0x0af1, 0x1ad0,
    0x2ab3, 0x3a92, 0xfd2e, 0xed0f, 0xdd6c, 0xcd4d,
    0xbdaa, 0xad8b, 0x9de8, 0x8dc9, 0x7c26, 0x6c07,
    0x5c64, 0x4c45, 0x3ca2, 0x2c83, 0x1ce0, 0x0cc1,
    0xef1f, 0xff3e, 0xcf5d, 0xdf7c, 0xaf9b, 0xbfba,
    0x8fd9, 0x9ff8, 0x6e17, 0x7e36, 0x4e55, 0x5e74,
    0x2e93, 0x3eb2, 0x0ed1, 0x1ef0
  ];

  const calculateCRC16 = (str) => {
    let crc = 0xFFFF;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      if (c > 255) throw new RangeError("Invalid character in string");
      const j = (c ^ (crc >> 8)) & 0xFF;
      crc = crcTable[j] ^ (crc << 8);
    }
    return ((crc ^ 0) & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
  };

  const padLeft = (str, n) => str.toString().padStart(n, '0');

  // Generate PayNow QR - Exact WISE format that works
  const generatePayNowQRData = () => {
    const amountStr = amount.toFixed(2);
    
    const payload = [
      { id: '00', value: '01' },
      { id: '01', value: '12' },
      {
        id: '26', value: [
          { id: '00', value: 'SG.PAYNOW' },
          { id: '01', value: '3' },
          { id: '02', value: 'UEN201422384R#WISE' },
          { id: '03', value: '1' }
        ]
      },
      { id: '52', value: '0000' },
      { id: '53', value: '702' },
      { id: '54', value: amountStr },
      { id: '58', value: 'SG' },
      { id: '59', value: 'NA' },
      { id: '60', value: 'SINGAPORE' },
      {
        id: '62', value: [
          { id: '01', value: 'A21882030QR' }
        ]
      }
    ];
    
    let qrString = payload.reduce((result, current) => {
      let value = current.value;
      if (Array.isArray(value)) {
        value = value.reduce((nested, item) => {
          return nested + item.id + padLeft(item.value.length, 2) + item.value;
        }, "");
      }
      return result + current.id + padLeft(value.length, 2) + value;
    }, "");
    
    qrString += '6304' + calculateCRC16(qrString + '6304');
    return qrString;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePaymentConfirmed = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/paynow/confirm`, {
        transaction_id: transactionId,
        payment_reference: paymentReference,
        account_holder_name: accountHolderName.trim() || null
      });
      
      setStatus("verifying");
      toast.success("Payment submitted for verification!");
      
      // Redirect after short delay
      setTimeout(() => {
        navigate(`/academy/registration-success?session_id=${transactionId}&method=paynow`);
      }, 1500);
    } catch (error) {
      console.error("Error confirming payment:", error);
      toast.error("Failed to confirm. Please try again or contact support.");
      setSubmitting(false);
    }
  };

  if (!paymentReference || !amount) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-xl font-bold text-red-400 mb-4">Invalid Payment Link</h1>
          <p className="text-slate-400 mb-4">This payment link is invalid or has expired.</p>
          <Link to="/"><Button>Return to Home</Button></Link>
        </Card>
      </div>
    );
  }

  const qrData = generatePayNowQRData();

  return (
    <div className="min-h-screen bg-[#0a0f1a] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back nav */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm"
          data-testid="paynow-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white">PayNow Payment</h1>
          <p className="text-slate-400">Scan the QR code with your banking app</p>
        </div>

        {/* Timer */}
        <Card className="p-3 mb-4 bg-orange-500/10 border-orange-500/30">
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-orange-400">
              Complete payment within: <strong>{formatTime(timeRemaining)}</strong>
            </span>
          </div>
        </Card>

        {/* Amount & Reference */}
        <Card className="p-4 mb-4">
          <div className="text-center mb-4">
            <p className="text-sm text-slate-400">Amount to Pay</p>
            <p className="text-3xl font-bold text-emerald-400">${amount.toFixed(2)} SGD</p>
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded border border-blue-500/30">
            <div>
              <p className="text-xs text-blue-400">Payment Reference</p>
              <p className="font-mono font-bold text-blue-300">{paymentReference}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(paymentReference)}>
              <Copy className="w-4 h-4 mr-1" />
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </Card>

        {/* QR Code */}
        <Card className="p-6 mb-4">
          <div className="text-center">
            <div className="inline-block p-4 bg-slate-900/50 rounded-lg shadow-inner mb-4">
              <QRCodeSVG value={qrData} size={200} level="M" includeMargin={true} />
            </div>
            <p className="text-sm text-slate-400">
              Scan with DBS, OCBC, UOB, or any PayNow app
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Recipient: <strong>Wise Asia Pacific Pte Ltd</strong>
            </p>
          </div>
        </Card>

        {/* Reference warning — red alert so parents do NOT overwrite the Wise reference */}
        <Card
          className="p-4 mb-4 bg-red-500/10 border-2 border-red-500/60"
          data-testid="paynow-ref-warning"
        >
          <div className="flex items-start gap-2">
            <span className="text-red-400 text-lg leading-none mt-0.5">⚠️</span>
            <div className="text-sm text-red-300">
              <p className="font-semibold text-red-300">
                CRITICAL: Do NOT change the Reference field in your banking app.
              </p>
              <p className="mt-2 text-red-200/95">
                If you edit the reference, <strong>Wise will reject the transfer and return the funds to your account.</strong> Your bank will show the money as deducted, but we will never receive it — and you'll have to pay again.
              </p>
              <p className="mt-2 text-red-300/90">
                The QR has already filled in the correct reference. Leave it exactly as-is.
              </p>
            </div>
          </div>
        </Card>

        {/* Instructions */}
        <Card className="p-4 mb-4 bg-yellow-500/10 border-yellow-500/30">
          <h3 className="font-semibold text-yellow-400 mb-2">How to Pay</h3>
          <ul className="text-sm text-yellow-400 space-y-1">
            <li>1. Scan QR code with your banking app</li>
            <li>2. Verify recipient shows: <strong>Wise Asia Pacific</strong></li>
            <li>3. Confirm the amount: <strong>${amount.toFixed(2)}</strong></li>
            <li>4. Complete the payment</li>
            <li>5. Enter your bank account name below <strong>(required)</strong></li>
            <li>6. Click the confirm button</li>
          </ul>
        </Card>

        {/* Invoice Notice */}
        <Card className="p-3 mb-4 bg-blue-500/10 border-blue-500/30">
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-400">
              <p><strong>Invoice</strong> has been emailed to you with payment details.</p>
              <p className="mt-1"><strong>Receipt</strong> will be sent once your payment is verified by our team.</p>
            </div>
          </div>
        </Card>

        {/* Confirm Section */}
        {status === "pending" && (
          <Card className="p-4 mb-4">
            <h3 className="font-semibold mb-3">After Payment - Confirm Here</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Your Bank Account Name <span className="text-red-400">*</span>
              </label>
              <Input
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
                placeholder="e.g., John Tan Wei Ming"
                className={`mb-1 ${!accountHolderName.trim() ? 'border-red-500/50' : 'border-emerald-500/50'}`}
                data-testid="bank-account-name-input"
              />
              <p className="text-xs text-red-400 font-medium">
                Required — enter the exact name on your bank account so we can match your payment
              </p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-300 mb-1">
                Payment screenshot <span className="text-slate-500">(optional, speeds up verification)</span>
              </p>
              {paymentReference && <PaymentProofUpload reference={paymentReference} />}
            </div>

            <Button 
              onClick={handlePaymentConfirmed}
              disabled={submitting || !accountHolderName.trim()}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="confirm-payment-btn"
            >
              {submitting ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  I Have Completed Payment
                </>
              )}
            </Button>
          </Card>
        )}

        {status === "verifying" && (
          <Card className="p-6 mb-4 bg-emerald-500/10 border-green-500/30">
            <div className="text-center">
              <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h3 className="font-bold text-emerald-400 text-lg">Payment Submitted!</h3>
              <p className="text-emerald-400 text-sm">Redirecting to confirmation page...</p>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex flex-col gap-2 text-center">
          <Button variant="outline" onClick={() => window.history.back()}>
            ← Go Back
          </Button>
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-300">
            Cancel and return home
          </Link>
        </div>
      </div>
    </div>
  );
}
